import { Button, makeStyles, Tooltip } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useParams } from '@reach/router';
import clsx from 'clsx';
import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { allowedReportYearRange } from '../../../../../components/Inputs/YearRange/YearRange';
import LoadingContainer from '../../../../../components/LoadingContainer';
import { useAuth } from '../../../../../contexts/AuthContext/AuthContext';
import {
  benchwiseReprocess,
  downloadBenchwiseDocx,
  downloadCustomReport,
  loadLatestBenchwiseReport,
} from '../../../../../core/api/benchmark';
import { hasBenchwiseAccess } from '../../../../../core/utils/access.utils';
import { formatUtcToLocalString } from '../../../../../core/utils/date.utils';
import { useErrorsDispatch } from '../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../FinancialData/shared/ErrorContext/ErrorContext.types';

interface Message {
  role: 'user' | 'user_action' | 'bot' | 'error';
  content: string;
  timestamp?: Date;
}

const POLLING_INTERVAL = 10000;
const RELOAD_STAGES = [
  'FAST_REVIEW',
  'FULL_PROGRESS',
  'INIT',
  'INIT_PROGRESS',
  'QUICK',
  // 'REVIEW_DONE',
  'SLOW_REVIEW',
];

const USER_ACTIONS = [
  'MANUAL_RESOLVE',
  'AUTO_EVALUATE',
  'SEARCH_CHANGE',
  'PLI_CHANGE',
  'CALCULATION_CHANGE',
  'REFINEMENT_SEARCH_ID_UPDATE',
  'NAME_CHANGE',
  'TESTED_PARTY_CHANGE',
  'INTEREST_RATE_CHANGE',
  'UNDEFINED',
];

const REGENERATE_ALLOWED_STAGES = [
  'NONE',
  // disbled for now
  // 'BENCHMARK_CHANGE',
  //  'DONE',
  //  'INIT',
  //  'REVIEW_DONE'
];

const NOT_DONE_MESSAGE =
  'The analysis is currently in progress — preliminary results are shown in the report below and will update automatically once complete.';
const BenchwiseReportTab: React.FC = () => {
  const { t } = useTranslation();
  // const { activeBenchmark } = useBenchmarkState();
  const { benchmarkId } = useParams();
  const classes = useStyles();
  const dispatchErrors = useErrorsDispatch();
  const [input, setInput] = useState<string>('');
  const [content, setContent] = useState<string>(t('benchmark:benchwise.defaultContent') || '');
  const [messages, setMessages] = useState<Message[]>([]);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const [benchwiseEnabled, setBenchwiseEnabled] = useState<boolean>(false);
  // currently not used, should be enabled later
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  // useRef to ensure study description is added only once
  const studyDescriptionAddedRef = useRef<boolean>(false);
  // useRefs for safe timer handling after handleSend
  const isMountedRef = useRef<boolean>(true);
  const [latestStage, setLatestStage] = useState<string | undefined>(undefined);
  // Polling function modified to set its own timeout if latestStage !== 'DONE'
  // Polling function modified to optionally work silently (without setting reportLoading)
  const fetchBenchmarkReport = useCallback(
    async (silent = false) => {
      if (!benchmarkId) return null;
      if (!silent) {
        setReportLoading(true);
      }
      let data;
      try {
        data = await loadLatestBenchwiseReport(benchmarkId);
        if (isMountedRef.current && data) {
          setContent(data.latestReport!);
          setLatestStage(data.latestStage);
          // --- NEW HISTORY HANDLING ---
          if (Array.isArray(data.history) && data.history.length > 0) {
            const newMessages = data.history.map((record) => {
              let role: 'user' | 'bot' | 'user_action' = 'bot';
              let content = record.content || '';
              if (record.role === 'USER' || record.role === 'USER_ACTION') {
                if (record.role === 'USER_ACTION' && record.content) {
                  // split the content by newlines and if it matches a user action, translate it
                  content = record.content
                    .split('\n')
                    .map((line) =>
                      USER_ACTIONS.includes(line)
                        ? t('benchmark:benchwise.chatmessage.userAction.' + line)
                        : line,
                    )
                    .join('\n');
                  role = 'user_action';
                } else if (record.role === 'USER') {
                  role = 'user';
                } else {
                  role = 'user_action';
                }
              } else {
                content =
                  record.stage === 'REASONING'
                    ? record.content || ''
                    : t('benchmark:benchwise.chatmessage.' + record.stage || 'default');
              }
              return {
                role,
                content: content,
                timestamp: record.timestamp ? new Date(record.timestamp) : undefined,
              };
            });
            setMessages(() => newMessages);
            studyDescriptionAddedRef.current = true;
          } else if (data.studyDescription && !studyDescriptionAddedRef.current) {
            // fallback to old logic if no history
            const newMessages = data.studyDescription.split('\n').reduce<Message[]>((acc, line) => {
              const trimmedLine = line.trim();
              if (trimmedLine) {
                acc.push({ role: 'user', content: trimmedLine });
              }
              return acc;
            }, []);
            if (newMessages.length) {
              setMessages((prev) => [...prev, ...newMessages]);
            }
            studyDescriptionAddedRef.current = true;
          }
          if (data.latestStage && RELOAD_STAGES.includes(data.latestStage)) {
            setLoadingMessage(NOT_DONE_MESSAGE);
          } else {
            const botText =
              data.latestStage === 'DONE'
                ? 'The analysis is complete and ready for your review. You may revisit any step of the analysis, and the study can be regenerated to reflect your changes.'
                : data.latestStage === 'BENCHMARK_CHANGE' || data.latestStage === 'REVIEW_DONE'
                ? 'The study has been changed. You can regenerate the study to reflect the changes made to the benchmark.'
                : '';
            const botMessage: Message = {
              role: 'bot',
              content: botText,
            };
            setMessages((prev) => [...prev, botMessage]);
            setLoadingMessage('');
          }
        }
      } catch (error) {
        if (isMountedRef.current) {
          // Extract error message from response
          const errorMessage =
            (error as any)?.response?.data?.detail ||
            (error as any)?.response?.data?.error ||
            (error as any)?.message ||
            'Unable to load the benchmarking report. Please try again.';

          const errorChatMessage: Message = {
            role: 'error',
            content: errorMessage,
          };
          setMessages((prev) => [...prev, errorChatMessage]);

          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        }
      } finally {
        if (isMountedRef.current && !silent) {
          setReportLoading(false);
        }
      }
      // Schedule the next poll if the report is not done.
      if (
        isMountedRef.current &&
        data &&
        data.latestStage &&
        RELOAD_STAGES.includes(data.latestStage) &&
        data.latestStage !== 'NONE'
      ) {
        setTimeout(() => fetchBenchmarkReport(true), POLLING_INTERVAL);
      }
      return data;
    },
    [benchmarkId, dispatchErrors, t],
  );

  // Start initial polling once benchmarkId is available.
  useEffect(() => {
    setBenchwiseEnabled(hasBenchwiseAccess(user));
    if (benchmarkId) {
      fetchBenchmarkReport();
    }
  }, [benchmarkId, fetchBenchmarkReport, user]);

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSend = async () => {
    if (input.trim() === '') return;
    const userMessage: Message = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setLoadingMessage('Performing benchmarking study...');

    try {
      const data = await benchwiseReprocess(benchmarkId, input);

      if (data) {
        setContent(data.latestReport!);
        if (data.latestStage !== 'DONE') {
          const stillInProgressMessage: Message = {
            role: 'bot',
            content: NOT_DONE_MESSAGE,
          };
          setMessages((prev) => [...prev, stillInProgressMessage]);
        } else {
          const botMessage: Message = {
            role: 'bot',
            content: 'Please find the benchmarking study below:',
          };
          setMessages((prev) => [...prev, botMessage]);
        }
        // Invalidate the DONE state by restarting the polling chain.
        fetchBenchmarkReport(true);
      }
    } catch (error) {
      // Extract error message from response
      const errorMessage =
        (error as any)?.data?.detail ||
        (error as any)?.response?.data?.detail ||
        (error as any)?.response?.data?.error ||
        (error as any)?.message ||
        'Unable to generate the study. Please try again.';

      const errorChatMessage: Message = {
        role: 'error',
        content: errorMessage,
      };
      setMessages((prev) => [...prev, errorChatMessage]);

      // Also dispatch to error context for snackbar
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey && benchwiseEnabled && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownloadDocx = async () => {
    try {
      await downloadBenchwiseDocx(benchmarkId);
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    }
  };

  const handleDownloadCustomDocx = async () => {
    try {
      await downloadCustomReport(benchmarkId, {
        finDataYearRange: allowedReportYearRange,
        reportFieldIds: [],
      });
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    }
  };

  const handleNewStudy = async () => {
    // TODO: Implement actual new study creation logic
    // For now, just show a placeholder message
    setLoading(true);
    setLoadingMessage('Creating new study...');
    try {
      // You can implement the API call here to create a new study
      // and pre-populate with current study and input
      // For now, just simulate
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage('');
        setMessages((prev) => [
          ...prev,
          { role: 'bot', content: 'New study created (placeholder).' },
        ]);
      }, 1200);
    } catch (error) {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const disableRegenerate =
    !!(window as any)?.env?.DISABLE_REGENERATE &&
    !!latestStage &&
    !REGENERATE_ALLOWED_STAGES.includes(latestStage);

  // Helper function to generate slug from heading text
  const generateSlug = (text: any): string => {
    if (text == null || text === '') {
      return '';
    }
    return String(text)
      .toLowerCase()
      .trim()
      .replace(/^\d+\.\s*/, '') // Remove leading numbers like "1. "
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  if (reportLoading) {
    return <LoadingContainer />;
  }

  // --- CHAT RENDERING WITH TIMESTAMP ---
  return (
    <div className={classes.appContainer}>
      <div className={classes.chatContainer}>
        {messages.map((message, index) => (
          <div
            key={index}
            className={clsx(
              classes.message,
              message.role === 'user' || message.role === 'user_action'
                ? classes.userMessage
                : message.role === 'error'
                ? classes.errorMessage
                : classes.botMessage,
            )}
          >
            <div>{message.content}</div>
            {message.timestamp && (
              <div className={classes.timestamp}>{formatUtcToLocalString(message.timestamp)}</div>
            )}
          </div>
        ))}
        {loading && <div className={`${classes.message} bot`}>Loading...</div>}
      </div>
      <div className={classes.inputContainer}>
        <textarea
          className={classes.textarea}
          rows={disableRegenerate ? 1 : 4}
          value={input}
          onChange={handleInputChange}
          placeholder="Provide some additional info..."
          disabled={disableRegenerate || !benchwiseEnabled || loading}
          onKeyDown={handleKeyPress}
        />
        <div className={classes.buttonRow}>
          <Tooltip
            title={
              disableRegenerate
                ? 'This feature is currently under development. Please reset the study or start a new one to proceed.'
                : 'Re-write the report using the extra details.'
            }
            arrow
          >
            <span>
              <Button
                className={classes.button}
                onClick={handleSend}
                disabled={
                  disableRegenerate ||
                  !benchwiseEnabled ||
                  loading ||
                  (!!latestStage && !REGENERATE_ALLOWED_STAGES.includes(latestStage))
                }
                startIcon={
                  <span role="img" aria-label="regenerate" style={{ fontSize: '1.2em' }}>
                    ↻
                  </span>
                }
              >
                Regenerate Report
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title="Create a new study based on the previous and new instructions. New search strategy will be generated. Note that any manual changes will not be carried over. "
            arrow
          >
            <span>
              <Button
                className={classes.outlineButton}
                onClick={handleNewStudy}
                disabled={true}
                variant="outlined"
                startIcon={
                  <span role="img" aria-label="new study" style={{ fontSize: '1.2em' }}>
                    ➕
                  </span>
                }
              >
                New Study
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
      <div className={classes.container}>
        {loadingMessage && (
          <div className={classes.statusContainer}>
            <div className={classes.statusItem}>{loadingMessage}</div>
          </div>
        )}
        {!loading && !!latestStage && latestStage !== 'NONE' && (
          <div className={clsx(classes.generatedContent, loadingMessage && classes.greyedOut)}>
            <div className={classes.generatedHeader}>
              <span className={classes.headerTitle}>Benchmarking study report</span>
              <div className={classes.downloadButtonsContainer}>
                {latestStage === 'DONE' && content && (
                  <Button
                    className={classes.downloadDocxButton}
                    onClick={handleDownloadDocx}
                    startIcon={<GetAppIcon />}
                  >
                    Download DOCX
                  </Button>
                )}
                {latestStage === 'DONE' && content && user?.hasCustomReport && (
                  <Button
                    className={classes.downloadDocxButton}
                    onClick={handleDownloadCustomDocx}
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                  >
                    Custom DOCX
                  </Button>
                )}
              </div>
            </div>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({ children, ...props }) => (
                  <div className={classes.tableWrapper}>
                    <table {...props}>{children}</table>
                  </div>
                ),
                h1: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h1 {...props} id={id}>
                      {children}
                    </h1>
                  );
                },
                h2: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h2 {...props} id={id}>
                      {children}
                    </h2>
                  );
                },
                h3: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h3 {...props} id={id}>
                      {children}
                    </h3>
                  );
                },
                h4: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h4 {...props} id={id}>
                      {children}
                    </h4>
                  );
                },
                h5: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h5 {...props} id={id}>
                      {children}
                    </h5>
                  );
                },
                h6: ({ children, ...props }) => {
                  const id = generateSlug(children);
                  return (
                    <h6 {...props} id={id}>
                      {children}
                    </h6>
                  );
                },
                a: ({ children, href, ...props }) => {
                  const isInternalLink = href?.startsWith('#');

                  if (isInternalLink && href) {
                    return (
                      <a
                        {...props}
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          const id = href.substring(1);
                          const element = document.getElementById(id);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                      >
                        {children}
                      </a>
                    );
                  }

                  return (
                    <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  );
                },
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '1000px',
    minWidth: '800px',
    width: '100%',
    padding: '20px',
    margin: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    // boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#e9ecef',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  statusItem: {
    margin: '20px 0',
    padding: '10px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '600px',
    textAlign: 'center',
    fontSize: '16px',
    color: '#333',
  },
  button: {
    background: 'linear-gradient(90deg, #38d430 0%, #7ee8fa 30%, #4176fa 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 400,
    letterSpacing: '1px',
    boxShadow: '0 2px 8px rgba(54, 209, 255, 0.10)',
    cursor: 'pointer',
    transition: 'background 0.3s, transform 0.3s',
    height: 40,
    '&:hover': {
      background: 'linear-gradient(90deg, #2ebd3a 0%, #5ecbfa 50%, #345eea 100%)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      background: 'linear-gradient(90deg, #24992e 0%, #4bb3e6 50%, #274bbd 100%)',
      transform: 'translateY(0)',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(54, 209, 255, 0.3)',
    },
  },
  appContainer: {
    maxWidth: '1000px',
    minWidth: '800px',
    width: '100%',
    padding: '20px',
    margin: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  chatContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: 1,
    overflowY: 'auto',
    marginBottom: '10px',
    width: '100%',
  },
  message: {
    padding: '10px',
    marginBottom: '10px',
    borderRadius: '8px',
    maxWidth: '80%',
    minWidth: '70%',
    width: '100%',
  },
  timestamp: {
    fontSize: '0.8em',
    color: '#888',
    marginTop: 4,
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#dcf8c6',
  },
  botMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#f1f0f0',
    textAlign: 'right',
    marginLeft: 'auto',
  },
  errorMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#ffebee',
    border: '1px solid #ef5350',
    color: '#c62828',
    textAlign: 'right',
    marginLeft: 'auto',
    fontWeight: 500,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexDirection: 'column',
  },
  generatedHeader: {
    position: 'relative',
    background: 'linear-gradient(90deg, #4a90e2 0%, #38d430 100%)',
    borderRadius: '16px 16px 0 0',
    color: '#fff',
    fontSize: '2.2rem',
    fontWeight: 500,
    width: 'calc(100% + 40px)', // 2 * appContainer padding
    padding: '28px 0',
    margin: '0 0 0 -20px', // negative left margin to offset appContainer padding
    textAlign: 'center',
    letterSpacing: '0.5px',
    left: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: 'radial-gradient(rgba(128,128,128,0.13) 2px, transparent 2px)',
      backgroundSize: '8px 8px',
      borderRadius: '8px 8px 0 0',
      pointerEvents: 'none',
    },
  },
  headerTitle: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    zIndex: 1,
    position: 'relative',
  },
  downloadDocxButton: {
    marginBottom: 3,
    //position: 'static',
    //top: 16,
    //right: 32,
    background: '#f5f5f5',
    color: '#4176fa',
    fontWeight: 500,
    borderRadius: 8,
    boxShadow: 'none',
    border: '1px solid #4176fa',
    height: 40,
    zIndex: 2,
    alignSelf: 'auto',
    '&:hover': {
      background: '#e3eefd',
      borderColor: '#345eea',
    },
  },
  generatedContent: {
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      fontFamily: 'Arial, sans-serif',
      margin: '20px 0',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Added subtle shadow
    },
    '& thead th': {
      backgroundColor: '#4CAF50', // Changed to a modern green color
      color: '#fff',
      padding: '12px 15px',
      textAlign: 'left',
    },
    '& tbody tr': {
      borderBottom: '1px solid #ddd', // Added border for rows
    },
    '& tbody tr:nth-child(2n)': {
      backgroundColor: '#f9f9f9', // Lighter alternate row color
    },
    '& tbody tr:nth-child(2n+1)': {
      backgroundColor: '#fff', // White alternate row color
    },
    '& tbody tr:hover': {
      backgroundColor: '#f1f1f1', // Highlight row on hover
    },
    '& tbody td': {
      padding: '12px 15px',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  greyedOut: {
    opacity: 0.5,
    filter: 'grayscale(0.5)',
    pointerEvents: 'none',
  },
  textarea: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    outline: 'none',
    fontSize: '1rem',
    resize: 'vertical',
    minHeight: '60px',
    maxHeight: '180px',
    width: '100%',
    marginBottom: '10px',
    transition: 'border-color 0.3s',
    '&:focus': {
      borderColor: '#007bff',
    },
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '0',
    width: '100%',
    justifyContent: 'flex-end',
  },
  outlineButton: {
    border: '1.5px solid #4176fa',
    color: '#4176fa',
    background: '#fff',
    borderRadius: '8px',
    fontWeight: 500,
    height: 40,
    boxShadow: 'none',
    '&:hover': {
      background: '#e3eefd',
      borderColor: '#345eea',
    },
  },
  downloadButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    position: 'absolute',
    top: 16,
    right: 32,
    zIndex: 2,
    alignItems: 'flex-end',
  },
}));

export default BenchwiseReportTab;
